import { MeshProperties, Mesh, MeshFields } from "@/types/core/Mesh";
import { Vector3 } from "three";
import * as THREE from "three";
import { Capsule } from "@react-three/drei";
import { forwardRef, useRef, useMemo } from "react";
import { TransformableItemProperties } from '../../core/Item';
import { Euler } from "three";
import { useAppContext } from "@/context/AppContext";
import { Material } from "@/types/core/Material";

export interface CapsuleMeshProperties extends TransformableItemProperties {
  radius: number;
  length: number;
  capSegments: number;
  radialSegments: number;
  materialId?: string;
}

export interface CapsuleMesh extends Mesh<CapsuleMeshProperties> {
  subtype: 'capsule';
  properties: CapsuleMeshProperties;
}

// Create a reusable Capsule component that can be used in both preview and world
const CapsuleComponent = forwardRef<THREE.Mesh, { properties: CapsuleMeshProperties; isPreview?: boolean }>((props, ref) => {
  const { properties, isPreview } = props;
  const { radius, length, capSegments, radialSegments, transform, materialId } = properties;
  const { activeWorld } = useAppContext();
  
  // In preview mode, use simplified transform
  const position: [number, number, number] = isPreview 
    ? [0, 0, 0] 
    : [transform.position.x, transform.position.y, transform.position.z];
  const rotation: [number, number, number] = isPreview
    ? [0, 0, 0]
    : [transform.rotation.x, transform.rotation.y, transform.rotation.z];
  const scale: [number, number, number] = isPreview
    ? [1, 1, 1]
    : [transform.scale.x, transform.scale.y, transform.scale.z];

  // Get the material from the world if available
  const materialItem = activeWorld && materialId ? activeWorld.items[materialId] : undefined;
  const material = materialItem?.type === 'material' ? (materialItem as unknown as Material) : undefined;

  // Create the material using the render function
  const meshMaterial = useMemo(() => {
    if (material) {
      return material.render(material.properties, isPreview);
    }
    return new THREE.MeshStandardMaterial({ color: "#808080" });
  }, [material, material?.properties, isPreview]);

  return (
    <Capsule
      ref={ref}
      args={[radius, length, capSegments, radialSegments]}
      position={position}
      rotation={rotation}
      scale={scale}
      material={meshMaterial}
    />
  );
});

export const CapsuleMeshItem: CapsuleMesh = {
  id: '',
  name: 'Capsule',
  type: 'mesh',
  subtype: 'capsule',
  icon: 'Capsule',
  properties: {
    radius: 1,
    length: 2,
    capSegments: 8,
    radialSegments: 16,
    transform: {
      position: new Vector3(),
      rotation: new Euler(),
      scale: new Vector3(1, 1, 1)
    },
    materialId: ''
  },
  fields: [
    ...MeshFields,
    {
      key: 'properties.radius',
      type: 'number',
      label: 'Radius',
      default: 1,
      panel: 'geometry'
    },
    {
      key: 'properties.length',
      type: 'number',
      label: 'Length',
      default: 2,
      panel: 'geometry'
    },
    {
      key: 'properties.capSegments',
      type: 'number',
      label: 'Cap Segments',
      default: 8,
      panel: 'geometry'
    },
    {
      key: 'properties.radialSegments',
      type: 'number',
      label: 'Radial Segments',
      default: 16,
      panel: 'geometry'
    }
  ],
  render: (properties: CapsuleMeshProperties, isPreview: boolean, ref: React.RefObject<THREE.Object3D | null>) => {
    return <CapsuleComponent properties={properties} isPreview={isPreview} ref={ref as React.RefObject<THREE.Mesh>} />;
  }
}; 